// R115 calendar hub — the INSPECTION entity: chip kind (quick popover with the
// one-tap assign, desktop drag = reschedule), the inspector panel (summary +
// one blue "Assign" + Move / Not possible / Cancel / Undo / Open in Inspections).
// Writes go through the Inspections module's own routes, tagged source:'calendar':
//   POST /api/assetops/inspections/assign      { listing_id, checkout, inspector, override? }
//   POST /api/assetops/inspections/queue/move  { listing_id, from, to, kind, by, override? }
//   POST /api/assetops/inspections/queue/skip  { listing_id, checkout_date, reason, note, by, clear? }
// Every 409 needs_confirm is answered INLINE (never window.confirm in a popover).
import { registerInspector, registerSection, registerAction, registerChipKind, type CalendarCtx, type EntityRef } from '../registry';
import { inspKindLabel, inspStateLabel, moveKind, canMoveInspection, canDropInspection, jobOpen, isGhost, type InspJob, type InspLayer } from '../../../../../lib/calendarOps.mjs';
import { OFFICE, views, insp, findJob, propName, postJson, busy, sheetHtml, trackDirty, say, pill, oneShot, type Res } from './util';

const ASSIGN = '/api/assetops/inspections/assign';
const MOVE = '/api/assetops/inspections/queue/move';
const SKIP = '/api/assetops/inspections/queue/skip';
const ALL_VIEWS = views('stays', 'ops', 'ops.hk', 'ops.maint', 'guests', 'mtr', 'health');

const STATE_TONE: Record<string, string> = { unassigned: 'warn', assigned: 'info', in_progress: 'accent', done: 'ok', skipped: 'muted', moved: 'muted', not_inspected: 'bad' };

export function inspectorOptions(ctx: CalendarCtx, L: InspLayer | null, current: string | undefined, blank = '— unassigned —'): string {
  const pool = (L && L.inspectors) || [];
  const cur = current || '';
  const opts = [`<option value="">${ctx.esc(blank)}</option>`]
    .concat(pool.map(p => `<option value="${ctx.esc(p.name)}"${p.name === cur ? ' selected' : ''}>${ctx.esc(p.name)}${['admin', 'director', 'manager'].includes(p.role) ? ` (${ctx.esc(p.role)})` : ''}</option>`));
  if (cur && !pool.some(p => p.name === cur)) opts.push(`<option value="${ctx.esc(cur)}" selected>${ctx.esc(cur)}</option>`);
  return opts.join('');
}

// R115 review: taking the person off a 📌 (ad-hoc) inspection REMOVES it — the server deletes the entry (nothing else
// backs that day). Say so in the list, and ask before doing it.
const BLANK_FOR = (job: InspJob) => (job.kind === 'manual' ? 'Unschedule — removes this 📌 inspection' : '— unassigned —');
const unscheduleOk = (ctx: CalendarCtx, job: InspJob, name: string): boolean =>
  !!name || job.kind !== 'manual' || window.confirm(`Remove the 📌 inspection at ${propName(ctx, job.listing_id)} on ${ctx.fmt.wmd(job.date)}? It leaves the calendar and the Inspections queue.`);
/** The Inspections page's date view can't hold a missed / past job (it rolls into Today) — hand off a date only for today-or-later, on-time jobs. */
const queueDateFor = (ctx: CalendarCtx, j: InspJob | null, fallback?: string | null): string | null => {
  const d = (j && (j.date || j.roll_to)) || fallback || null;
  if (!d || (j && j.missed) || d < ctx.asOf) return null;
  return d;
};

const afterWrite = async (ctx: CalendarCtx, msg: string) => {
  ctx.toast(msg);
  await ctx.reload({ layers: ['inspections'], inspector: true });
};

/** Assign / reassign with the inline 409 handshake. Resolves true when saved. */
export async function assignInline(ctx: CalendarCtx, root: HTMLElement, job: InspJob, name: string, override = false): Promise<boolean> {
  const body: Record<string, unknown> = { listing_id: job.listing_id, checkout: job.date, inspector: name, source: 'calendar' };
  if (override) body.override = true;
  say(root, 'Saving…');
  const r: Res = await busy(() => postJson(ASSIGN, body));
  if (r.ok) { say(root, ''); return true; }
  if (r.status === 409 && r.json && r.json.needs_confirm) {
    const m = say(root, `${ctx.esc(r.error)} <button type="button" class="btn-secondary btn-sm" data-opv-anyway>Assign anyway</button>`, 'warn');
    return new Promise<boolean>(resolve => {
      const b = m && m.querySelector('[data-opv-anyway]') as HTMLButtonElement | null;
      if (!b) return resolve(false);
      b.addEventListener('click', async () => { b.disabled = true; resolve(await assignInline(ctx, root, job, name, true)); }, { once: true });
    });
  }
  if (r.status === 409 && r.json && r.json.moved_to) { say(root, `This inspection was moved to ${ctx.esc(ctx.fmt.md(r.json.moved_to))} — refresh the calendar.`, 'bad'); return false; }
  say(root, ctx.esc(r.error), 'bad');
  return false;
}

/** Reschedule (drag drop or the 21-day strip) through the shared needs_confirm helper. */
export async function moveJob(ctx: CalendarCtx, job: InspJob, to: string): Promise<void> {
  const body = { listing_id: job.listing_id, from: job.date, to, kind: moveKind(job), by: ctx.actorName() || 'office', source: 'calendar' };
  const r = await busy(() => ctx.postWithOverride(MOVE, body, (j: any) => String((j && j.error) || 'Move anyway?')));
  if (r.ok) {
    await afterWrite(ctx, `Inspection moved to ${ctx.fmt.wmd(to)}`);
    ctx.openInspector({ kind: 'inspection', id: `${job.listing_id}|${to}`, listing_id: job.listing_id, date: to });
  } else if (!(r.status === 409 && r.json && (r.json as any).needs_confirm)) {   // a declined "move anyway?" is not an error
    ctx.toast(String((r.json && (r.json as any).error) || r.error || 'Could not move the inspection'));
  }
}
const pickMove = (ctx: CalendarCtx, job: InspJob) => ctx.pickDay({
  listing_id: job.listing_id, label: `Move the inspection (${ctx.fmt.md(job.date)}) to…`, allowTaken: true,
  onPick: (d) => { if (canDropInspection(job, d, ctx.asOf)) void moveJob(ctx, job, d); else ctx.toast('Pick a day from today through 60 days out.'); },
});

function summaryHtml(ctx: CalendarCtx, j: InspJob): string {
  const rows: string[] = [];
  rows.push(`<div class="opv-pills">${pill(ctx, inspKindLabel(j), 'info')} ${pill(ctx, inspStateLabel(j), STATE_TONE[j.state] || 'muted')}${j.missed ? ` ${pill(ctx, '⏰ Missed — rolled into today', 'bad')}` : ''}</div>`);
  const kv = (k: string, v: string) => `<div class="opv-kv"><span>${ctx.esc(k)}</span><b>${v}</b></div>`;
  rows.push(kv('Day', ctx.esc(ctx.fmt.wmd(j.date))));
  rows.push(kv('Inspector', j.inspector ? ctx.esc(j.inspector) : '<span class="opv-muted">nobody yet</span>'));
  if (j.moved_from) rows.push(kv('Moved from', ctx.esc(ctx.fmt.wmd(j.moved_from))));
  if (j.state === 'moved' && j.moved_to) rows.push(kv('Moved to', `<button type="button" class="btn-ghost btn-sm" data-opv-goto="${ctx.esc(`${j.listing_id}|${j.moved_to}`)}">${ctx.esc(ctx.fmt.wmd(j.moved_to))} →</button>`));
  if (j.same_day) rows.push(`<div class="opv-note opv-warn">Same-day turnover — the next guest arrives that day.</div>`);
  if (j.guest_inside) rows.push(`<div class="opv-note opv-warn">A guest is staying that day — the inspection needs them out.</div>`);
  if (j.orphaned) rows.push(`<div class="opv-note opv-bad">The booking this job came from changed or was cancelled.</div>`);
  if (j.walked_early) rows.push(`<div class="opv-note opv-warn">Walked early on ${ctx.esc(ctx.fmt.md(j.walked_early))} while the guest was in — the post-checkout check is still owed.</div>`);
  if (j.state === 'not_inspected') rows.push(`<div class="opv-note opv-bad">Nobody inspected this turnover (an inspection after the next checkout doesn't count for it).</div>`);
  return `<div class="opv-sum">${rows.join('')}</div>`;
}

registerInspector({
  kind: 'inspection',
  title: (ref, data, ctx) => propName(ctx, (data as InspJob | null)?.listing_id || ref.listing_id),
  sub: (ref, data, ctx) => { const j = data as InspJob | null; return j ? `Inspection · ${ctx.fmt.wmd(j.date)} · ${inspKindLabel(j)}` : 'Inspection'; },
  load: async (ref, _signal, ctx) => findJob(ctx, ref.id),
});
registerSection({
  inspector: 'inspection', key: 'insp_summary', title: 'Inspection', order: 10, openIn: ALL_VIEWS,
  when: () => true,
  render: (ref, data, ctx) => (data ? summaryHtml(ctx, data as InspJob)
    : `<div class="opv-note">This inspection isn't in view any more (moved, cancelled or outside the loaded days). <button type="button" class="btn-ghost btn-sm" data-opv-open-insp>Open Inspections</button></div>`),
  bind: (root, ref, data, ctx) => {
    root.querySelectorAll<HTMLElement>('[data-opv-goto]').forEach(b => b.addEventListener('click', () => {
      const id = b.dataset.opvGoto || ''; const [lid, date] = [id.slice(0, id.lastIndexOf('|')), id.slice(id.lastIndexOf('|') + 1)];
      ctx.openInspector({ kind: 'inspection', id, listing_id: lid, date });
    }));
    root.querySelector('[data-opv-open-insp]')?.addEventListener('click', () => { const d = queueDateFor(ctx, null, ref.date); if (d) oneShot('insp_queue_date', d); ctx.go('hk-insp', 'inspections'); });
  },
});

// ── the assign sheet (the inspector's blue action) ─────────────────────────
function openAssignSheet(ctx: CalendarCtx, job: InspJob): void {
  const L = insp(ctx);
  const head = `<b>${ctx.esc(propName(ctx, job.listing_id))}</b><span>${ctx.esc(ctx.fmt.wmd(job.date))} · ${ctx.esc(inspKindLabel(job))}</span>`;
  const body = `<label class="opv-field"><span>Inspector</span><select data-opv-sel>${inspectorOptions(ctx, L, job.inspector, BLANK_FOR(job))}</select></label>`
    + (job.guest_inside ? `<div class="opv-note opv-warn">A guest is staying that day.</div>` : '')
    + (job.kind === 'manual'
      ? `<p class="opv-muted">This is a 📌 ad-hoc inspection — choosing “Unschedule” removes it (nothing else backs that day).</p>`
      : `<p class="opv-muted">Choosing “— unassigned —” takes the person off; the inspection stays scheduled.</p>`);
  const foot = `<button type="button" class="btn-secondary" data-opv-cancel>Cancel</button><button type="button" class="btn-primary" data-opv-save>Save</button>`;
  const root = ctx.openSheet(sheetHtml(ctx, head, body, foot), { title: job.inspector ? 'Reassign inspection' : 'Assign inspection', onClose: () => ctx.markDirty(false) });
  trackDirty(ctx, root);
  root.querySelector('[data-opv-cancel]')?.addEventListener('click', () => { ctx.markDirty(false); ctx.closeSheet(); });
  const save = root.querySelector('[data-opv-save]') as HTMLButtonElement | null;
  save?.addEventListener('click', async () => {
    const sel = root.querySelector('[data-opv-sel]') as HTMLSelectElement;
    if (!unscheduleOk(ctx, job, sel.value)) return;
    save.disabled = true;
    const ok = await assignInline(ctx, root, job, sel.value);
    save.disabled = false;
    if (ok) { ctx.markDirty(false); ctx.closeSheet(); await afterWrite(ctx, sel.value ? `${sel.value} will inspect ${propName(ctx, job.listing_id)}` : job.kind === 'manual' ? 'Inspection removed' : 'Inspector removed'); }
  });
}
// ── skip / cancel / undo ───────────────────────────────────────────────────
function openSkipSheet(ctx: CalendarCtx, job: InspJob, reason: 'other' | 'cancelled'): void {
  const head = `<b>${ctx.esc(propName(ctx, job.listing_id))}</b><span>${ctx.esc(ctx.fmt.wmd(job.date))} · ${ctx.esc(inspKindLabel(job))}</span>`;
  const body = (reason === 'cancelled'
    ? `<p>Cancel this inspection? It stays on the list greyed out, with an undo.</p>`
    : `<p>Mark this inspection <b>not possible</b> (a guest is inside / the home can't be walked). It stays on the list greyed out, with an undo.</p>`)
    + `<label class="opv-field"><span>Note (optional)</span><textarea rows="2" maxlength="300" data-opv-note placeholder="${reason === 'cancelled' ? 'Why it is cancelled' : 'Guest inside — rebook after checkout'}"></textarea></label>`;
  const foot = `<button type="button" class="btn-secondary" data-opv-cancel>Back</button><button type="button" class="btn-primary" data-opv-save>${reason === 'cancelled' ? 'Cancel inspection' : 'Mark not possible'}</button>`;
  const root = ctx.openSheet(sheetHtml(ctx, head, body, foot), { title: reason === 'cancelled' ? 'Cancel inspection' : 'Not possible', onClose: () => ctx.markDirty(false) });
  trackDirty(ctx, root);
  root.querySelector('[data-opv-cancel]')?.addEventListener('click', () => { ctx.markDirty(false); ctx.closeSheet(); });
  const save = root.querySelector('[data-opv-save]') as HTMLButtonElement | null;
  save?.addEventListener('click', async () => {
    const note = (root.querySelector('[data-opv-note]') as HTMLTextAreaElement).value.trim() || (reason === 'other' ? 'Guest inside (calendar)' : '');
    save.disabled = true; say(root, 'Saving…');
    const r = await busy(() => postJson(SKIP, { listing_id: job.listing_id, checkout_date: job.date, reason, note, by: ctx.actorName() || 'office', source: 'calendar' }));
    save.disabled = false;
    if (!r.ok) { say(root, ctx.esc(r.error), 'bad'); return; }
    ctx.markDirty(false); ctx.closeSheet();
    await afterWrite(ctx, reason === 'cancelled' ? 'Inspection cancelled' : 'Marked not possible');
  });
}
async function undoSkip(ctx: CalendarCtx, job: InspJob): Promise<void> {
  const r = await busy(() => postJson(SKIP, { listing_id: job.listing_id, checkout_date: job.date, clear: true, by: ctx.actorName() || 'office', source: 'calendar' }));
  if (!r.ok) { ctx.toast(r.error); return; }
  await afterWrite(ctx, 'Inspection back on');
}

const J = (data: unknown) => data as InspJob | null;
registerAction({
  key: 'insp_assign', inspector: 'inspection', label: 'Assign', icon: '👤', order: 10, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (ref, data) => { const j = J(data); return !!j && j.state === 'unassigned'; },
  run: (ref, data, ctx) => { const j = J(data); if (j) openAssignSheet(ctx, j); },
});
registerAction({
  key: 'insp_reassign', inspector: 'inspection', label: 'Reassign', icon: '👤', order: 10, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (ref, data) => { const j = J(data); return !!j && (j.state === 'assigned' || j.state === 'in_progress'); },
  run: (ref, data, ctx) => { const j = J(data); if (j) openAssignSheet(ctx, j); },
});
registerAction({
  key: 'insp_moved_to', inspector: 'inspection', label: 'Open where it moved', icon: '↪', order: 11, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (ref, data) => { const j = J(data); return !!j && isGhost(j) && !!j.moved_to; },
  run: (ref, data, ctx) => { const j = J(data)!; ctx.openInspector({ kind: 'inspection', id: `${j.listing_id}|${j.moved_to}`, listing_id: j.listing_id, date: j.moved_to }); },
});
registerAction({
  key: 'insp_move', inspector: 'inspection', label: 'Move…', icon: '📅', order: 20, roles: OFFICE,
  when: (ref, data, ctx) => canMoveInspection(J(data), ctx.asOf),
  run: (ref, data, ctx) => { const j = J(data); if (j) pickMove(ctx, j); },
});
registerAction({
  key: 'insp_skip', inspector: 'inspection', label: 'Not possible — guest inside', icon: '🚫', order: 30, roles: OFFICE,
  when: (ref, data) => { const j = J(data); return !!j && (j.state === 'unassigned' || j.state === 'assigned'); },
  run: (ref, data, ctx) => { const j = J(data); if (j) openSkipSheet(ctx, j, 'other'); },
});
registerAction({
  key: 'insp_cancel', inspector: 'inspection', label: 'Cancel inspection', icon: '✕', order: 40, roles: OFFICE,
  when: (ref, data) => { const j = J(data); return !!j && (j.kind === 'manual' || j.kind === 'moved') && (j.state === 'unassigned' || j.state === 'assigned'); },
  run: (ref, data, ctx) => { const j = J(data); if (j) openSkipSheet(ctx, j, 'cancelled'); },
});
registerAction({
  key: 'insp_unskip', inspector: 'inspection', label: 'Undo — put it back on', icon: '↩', order: 50, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (ref, data) => { const j = J(data); return !!j && j.state === 'skipped'; },
  run: (ref, data, ctx) => { const j = J(data); if (j) void undoSkip(ctx, j); },
});
registerAction({
  key: 'insp_open', inspector: 'inspection', label: 'Open in Inspections', icon: '↗', order: 60, roles: OFFICE,
  when: () => true,
  run: (ref, data, ctx) => { const j = J(data); const d = queueDateFor(ctx, j, ref.date); if (d) oneShot('insp_queue_date', d); ctx.go('hk-insp', 'inspections'); },   // missed / past → Today, where they roll in
});

// ── the chip: a quick popover (one-tap assign) + desktop drag = reschedule ──
registerChipKind({
  kind: 'inspection', legend: 'Inspection',
  open: (ref: EntityRef, anchor: HTMLElement, ctx: CalendarCtx) => {
    const j = findJob(ctx, ref.id);
    if (!j) { ctx.openInspector(ref); return; }
    const L = insp(ctx);
    const canAssign = jobOpen(j);
    const html = `<div class="opv-pop" role="dialog" aria-label="Inspection">`
      + `<div class="opv-pop-head"><b>${ctx.esc(propName(ctx, j.listing_id))}</b><span>🔍 ${ctx.esc(ctx.fmt.wmd(j.date))} · ${ctx.esc(inspKindLabel(j))}</span></div>`
      + `<div class="opv-pills">${pill(ctx, inspStateLabel(j), STATE_TONE[j.state] || 'muted')}${j.missed ? ` ${pill(ctx, '⏰ Missed', 'bad')}` : ''}${j.guest_inside ? ` ${pill(ctx, 'Guest inside', 'warn')}` : ''}</div>`
      + (canAssign ? `<div class="opv-pop-row"><select data-opv-sel aria-label="Inspector">${inspectorOptions(ctx, L, j.inspector, BLANK_FOR(j))}</select><button type="button" class="btn-primary btn-sm" data-opv-assign>${j.inspector ? 'Reassign' : 'Assign'}</button></div>` : '')
      + `<div class="opv-msg" data-opv-msg role="status" aria-live="polite"></div>`
      + `<div class="opv-pop-foot">${canMoveInspection(j, ctx.asOf) ? `<button type="button" class="btn-secondary btn-sm" data-opv-move>Move…</button>` : ''}<button type="button" class="btn-ghost btn-sm" data-opv-details>Details…</button></div></div>`;
    const root = ctx.openPop(anchor, html, { width: 300, cls: 'opv-pop-host' });
    root.querySelector('[data-opv-details]')?.addEventListener('click', () => { ctx.closePop(); ctx.openInspector(ref); });
    root.querySelector('[data-opv-move]')?.addEventListener('click', () => { ctx.closePop(); pickMove(ctx, j); });
    const btn = root.querySelector('[data-opv-assign]') as HTMLButtonElement | null;
    btn?.addEventListener('click', async () => {
      const sel = root.querySelector('[data-opv-sel]') as HTMLSelectElement;
      if (!unscheduleOk(ctx, j, sel.value)) return;
      btn.disabled = true;
      const ok = await assignInline(ctx, root, j, sel.value);
      btn.disabled = false;
      if (ok) { ctx.closePop(); await afterWrite(ctx, sel.value ? `${sel.value} will inspect ${propName(ctx, j.listing_id)}` : j.kind === 'manual' ? 'Inspection removed' : 'Inspector removed'); }
    });
  },
  move: {
    canDrop: (ref, date, ctx) => canDropInspection(findJob(ctx, ref.id), date, ctx.asOf),
    onDrop: async (ref, date, ctx) => { const j = findJob(ctx, ref.id); if (j) await moveJob(ctx, j, date); },
  },
});
